import { useCallback, useRef } from "react";
import { parseClockToSeconds } from "../lib/clockHelpers";
import {
  applyClockMachineAction,
  deriveClockState,
} from "../lib/clockStateMachine";

interface UseCockpitClockHandlersParams {
  cockpitLocked: boolean;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  currentPhase?: string;
  isVarActiveLocal: boolean;
  /**
   * PMA-COCK-144: the broadcast-/breakdown-derived flag (`breakdownVarActive
   * || isVarActiveLocal` in CockpitContext.tsx) — NOT `isVarActiveLocal`
   * alone, which resets on reload before the server-confirmed state
   * rehydrates. Feeds the clock-lifecycle state machine that guards the
   * global resume control; unrelated to `isVarActiveLocal`'s existing use
   * below (the Detener-while-VAR sequence, untouched).
   */
  isVarActive: boolean;
  isTimeoutActive: boolean;
  varTimeSeconds: number;
  globalClock: string;
  isGlobalClockRunning: boolean;
  beginIneffective: () => void;
  endIneffectiveIfNeeded: (mode: "EFFECTIVE") => void;
  setIsBallInPlay: (value: boolean) => void;
  handleGlobalClockStart: () => void;
  handleGlobalClockStop: () => void;
  handleGlobalClockStartVarSync: () => void;
  handleGlobalClockStopVarSync: () => void;
  handleVarToggleBase: (options: {
    currentGlobalSeconds: number;
    currentVarSeconds: number;
    isGlobalClockRunning: boolean;
  }) => void;
  logNeutralTimerEvent: (
    type: "VARStart" | "VARStop" | "TimeoutStart" | "TimeoutStop",
  ) => void;
  handleModeSwitch: (mode: "EFFECTIVE" | "INEFFECTIVE") => void;
  /**
   * PMA-COCK-154: fires when a VAR/Timeout START tap is rejected by
   * `neutralTimerStartBlocked` below — the operator-visible feedback for
   * what was previously a silent no-op. Same `useCockpitToast` mechanism
   * already used by `useCockpitSubstitutionFlow.ts`'s blocked-substitution
   * toast, threaded here rather than invented fresh.
   */
  showTimedToast: (message: string, timeoutMs?: number) => void;
  t: (key: string, fallback: string) => string;
}

interface UseCockpitClockHandlersResult {
  handleGlobalClockStartGuarded: () => void;
  handleGlobalClockStopGuarded: () => void;
  handleModeSwitchGuarded: (mode: "EFFECTIVE" | "INEFFECTIVE") => void;
  handleVarToggle: () => void;
  handleTimeoutToggle: () => void;
  showFieldResume: boolean;
}

export const useCockpitClockHandlers = ({
  cockpitLocked,
  clockMode,
  currentPhase,
  isVarActiveLocal,
  isVarActive,
  isTimeoutActive,
  varTimeSeconds,
  globalClock,
  isGlobalClockRunning,
  beginIneffective,
  endIneffectiveIfNeeded,
  setIsBallInPlay,
  handleGlobalClockStart,
  handleGlobalClockStop,
  handleGlobalClockStartVarSync,
  handleGlobalClockStopVarSync,
  handleVarToggleBase,
  logNeutralTimerEvent,
  handleModeSwitch,
  showTimedToast,
  t,
}: UseCockpitClockHandlersParams): UseCockpitClockHandlersResult => {
  const handleGlobalClockStartGuarded = useCallback(() => {
    if (cockpitLocked) return;
    handleGlobalClockStartVarSync();
    setIsBallInPlay(true);
    handleGlobalClockStart();
  }, [
    cockpitLocked,
    handleGlobalClockStartVarSync,
    handleGlobalClockStart,
    setIsBallInPlay,
  ]);

  const handleGlobalClockStopGuarded = useCallback(() => {
    if (cockpitLocked) return;
    // When VAR is active, mirror the VARStop sequence before halting the clock.
    // Without this, clockMode stays EFFECTIVE and showFieldResume never becomes
    // true — the operator has no path to resume effective time except toggling
    // VAR off manually (undiscoverable).
    if (isVarActiveLocal) {
      const currentVarSeconds = varTimeSeconds;
      const currentGlobalSeconds = parseClockToSeconds(globalClock);
      logNeutralTimerEvent("VARStop");
      handleVarToggleBase({
        currentGlobalSeconds,
        currentVarSeconds,
        isGlobalClockRunning,
      });
      handleModeSwitch("INEFFECTIVE");
    }
    handleGlobalClockStopVarSync();
    setIsBallInPlay(false);
    handleGlobalClockStop();
  }, [
    cockpitLocked,
    globalClock,
    handleGlobalClockStop,
    handleGlobalClockStopVarSync,
    handleModeSwitch,
    handleVarToggleBase,
    isGlobalClockRunning,
    isVarActiveLocal,
    logNeutralTimerEvent,
    setIsBallInPlay,
    varTimeSeconds,
  ]);

  // PMA-COCK-144: the clock-lifecycle state machine (clockStateMachine.ts).
  // Derived fresh on every render from the CURRENT clockMode/isVarActive/
  // isTimeoutActive values, then snapshotted into a ref rather than left as
  // a `useCallback`-closed-over value. `handleModeSwitchGuarded` below reads
  // `clockMachineStateRef.current` inside its body — never a value captured
  // by the callback's own dependency list — so a click always sees the
  // click-time-fresh state regardless of when this callback was last
  // recreated. This mirrors the codebase's own established pattern for the
  // exact same class of risk (`matchClockRef`/`clockSnapshotRef` in
  // useIneffectiveTime.ts/useMatchTimer.ts): a `useCallback` whose deps lag
  // a commit, compounded by a WS-ack round trip, has silently orphaned
  // events here before — see this repo's recorded incident on click-time
  // state reads. A ref read inside a callback body can never go stale this
  // way, independent of whether every dependency was remembered correctly.
  const clockMachineState = deriveClockState({
    clockMode,
    isVarActive,
    isTimeoutActive,
  });
  const clockMachineStateRef = useRef(clockMachineState);
  clockMachineStateRef.current = clockMachineState;

  const handleModeSwitchGuarded = useCallback(
    (mode: "EFFECTIVE" | "INEFFECTIVE") => {
      if (cockpitLocked) return;
      if (mode === "INEFFECTIVE") {
        beginIneffective();
        return;
      }
      // mode === "EFFECTIVE": the global "Reanudar Tiempo Efectivo" resume.
      // Reject outright while a scoped stoppage (VAR/timeout) is open — the
      // operator must close it via its own "Finalizar" control first. The
      // state machine is the rule; a disabled button (MatchTimerDisplay.tsx/
      // LoggerView.tsx) is only the affordance — this guard fires
      // independent of whatever DOM state a click somehow bypassed.
      const result = applyClockMachineAction(clockMachineStateRef.current, {
        type: "RESUME_EFFECTIVE",
      });
      if (!result.allowed) return;
      endIneffectiveIfNeeded(mode);
    },
    [beginIneffective, cockpitLocked, endIneffectiveIfNeeded],
  );

  // PMA-COCK-126: pressing VAR/Time Off while the ball is genuinely in play
  // (clockMode EFFECTIVE + global clock running) double-counts match time —
  // `computeIneffectiveBreakdown` treats these as pausing an ALREADY-ACTIVE
  // stoppage; with none active they open a neutral accumulation that runs in
  // parallel with effective time. Mirrors `neutralTimerDisabled` in
  // MatchTimerDisplay.tsx (defense in depth — the disabled button is the
  // affordance, this is the rule). Only gates the START transition: an
  // active VAR/timeout must always be stoppable.
  const neutralTimerStartBlocked =
    clockMode === "EFFECTIVE" && isGlobalClockRunning;

  const handleVarToggle = useCallback(() => {
    if (cockpitLocked) return;
    const nextActive = !isVarActiveLocal;
    if (nextActive && neutralTimerStartBlocked) {
      // PMA-COCK-154: the button that dispatched this click may be styled
      // as blocked but is deliberately NOT DOM-`disabled` for this specific
      // rejection (see MatchTimerDisplay.tsx) — a native `disabled` button
      // fires no click at all, which is exactly the "dead tap" the operator
      // reported. Tell them why instead of silently doing nothing.
      showTimedToast(
        t(
          "neutralTimerBlockedVarToast",
          "Stop play first before starting VAR.",
        ),
      );
      return;
    }
    const currentVarSeconds = varTimeSeconds;
    const currentGlobalSeconds = parseClockToSeconds(globalClock);
    logNeutralTimerEvent(nextActive ? "VARStart" : "VARStop");
    handleVarToggleBase({
      currentGlobalSeconds,
      currentVarSeconds,
      isGlobalClockRunning,
    });
    // After VARStop: persist clock_mode=INEFFECTIVE via PATCH /matches/{id}/clock-mode
    // so the "Reanudar Tiempo Efectivo" affordance is present immediately and survives
    // a full page reload (match.clock_mode rehydrates into local clockMode on mount).
    // handleModeSwitch already calls fetchMatch() internally.
    if (!nextActive) handleModeSwitch("INEFFECTIVE");
  }, [
    cockpitLocked,
    globalClock,
    handleModeSwitch,
    handleVarToggleBase,
    isGlobalClockRunning,
    isVarActiveLocal,
    logNeutralTimerEvent,
    neutralTimerStartBlocked,
    showTimedToast,
    t,
    varTimeSeconds,
  ]);

  const handleTimeoutToggle = useCallback(() => {
    if (cockpitLocked) return;
    const nextActive = !isTimeoutActive;
    if (nextActive && neutralTimerStartBlocked) {
      // PMA-COCK-154: same rationale as handleVarToggle above.
      showTimedToast(
        t(
          "neutralTimerBlockedTimeoutToast",
          "Stop play first before starting a timeout.",
        ),
      );
      return;
    }
    logNeutralTimerEvent(nextActive ? "TimeoutStart" : "TimeoutStop");
    // PMA-COCK-131: unlike VAR (whose own review duration is tracked
    // separately and excluded from ineffective time — only the post-VAR
    // "waiting to resume" gap is ineffective, hence VAR's mode switch fires
    // on STOP), a timeout's entire duration IS ineffective time. So the
    // switch fires on START here, and — per OQ-COCK-131a's decision — never
    // fires on TimeoutStop: resume to EFFECTIVE stays a separate, explicit
    // operator action via the existing "Reanudar Tiempo Efectivo" affordance,
    // exactly mirroring VAR's own no-auto-resume precedent.
    if (nextActive) handleModeSwitch("INEFFECTIVE");
  }, [
    cockpitLocked,
    handleModeSwitch,
    isTimeoutActive,
    logNeutralTimerEvent,
    neutralTimerStartBlocked,
    showTimedToast,
    t,
  ]);

  return {
    handleGlobalClockStartGuarded,
    handleGlobalClockStopGuarded,
    handleModeSwitchGuarded,
    handleVarToggle,
    handleTimeoutToggle,
    // Show the "Reanudar" button whenever the clock is in non-EFFECTIVE mode
    // and the phase isn't a halftime break.  cockpitLocked controls the
    // `disabled` state of the button (via the `locked` prop in
    // MatchTimerDisplay), NOT its visibility — so the button remains visible
    // even when locked, giving the user a clear affordance that resume is
    // possible once the lock clears.
    showFieldResume:
      clockMode !== "EFFECTIVE" &&
      currentPhase !== "HALFTIME" &&
      currentPhase !== "EXTRA_HALFTIME",
  };
};
